import ArrowDown16 from '@carbon/icons-react/es/arrow--down/16';
import { Tooltip } from 'antd';
import React, { FC, useCallback, useMemo, useRef } from 'react';
import { ReactComponent as AddRowBelowIcon } from 'assets/svgs/add-row-below.svg';
import { ReactComponent as FormInstaceIcon } from 'assets/svgs/form-instance-icon.svg';
import { ReactComponent as RecurringTaskTemplateIcon } from 'assets/svgs/recurring-task-template.svg';
import { ReactComponent as RecurringTaskIcon } from 'assets/svgs/recurring-task.svg';
import { ReactComponent as SaveTaskTemplateIcon } from 'assets/svgs/save-task-template.svg';
import { ReactComponent as SmartFill } from 'assets/svgs/smartfill.svg';
import { Button } from 'components/atoms/button';
import { IconShell } from 'components/atoms/icon-shell';
import { RichTextEditor } from 'components/atoms/rich-text-editor/rich-text-editor';
import styles from 'components/organisms/templates/commons/cke.module.css';
import {
  ProjectTemplateTask,
  ProjectTemplateTaskData,
  useProjectTemplateTask,
} from 'data/templates/projects';
import { useTranslation } from 'hooks/use-translation';
import { TaskMoreActions, useTemplateTaskCommonActions } from '../../commons';
import { CellRendererParams } from '../types';
import {
  FormIconWrapper,
  MoreActions,
  Status,
  TemplateIndicatorWrapper,
} from './styles';

const TaskNameCell: FC<
  { task: ProjectTemplateTask; level: number } & Omit<
    CellRendererParams,
    'data'
  > & { data: ProjectTemplateTaskData }
> = ({ task, level }) => {
  const { addSubTask, onAddTaskBelow } = useTemplateTaskCommonActions(task);
  const updateRef = useRef<RichTextEditor>(null);

  const setData = useCallback(() => {
    updateRef.current?.setData();
  }, []);

  const addSubtaskTooltipText = useTranslation(
    'templates.list.tooltip.add_subtask',
  );

  const handleAddSubTask = () => {
    addSubTask();
  };
  const handleAddTaskBelow = () => {
    onAddTaskBelow();
  };

  const hasVariable = useMemo(
    () => task.taskName.includes('class="simpleVariables"'),
    [task.taskName],
  );

  return (
    <>
      <IconShell
        icon={
          <Status
            progress={0}
            status={{
              label: 'To do',
              group: 1,
              color: 'blue',
            }}
            type={task.type}
          />
        }
        size="middle"
      />
      {task.formTemplateId ? (
        <FormIconWrapper>
          <FormInstaceIcon />
        </FormIconWrapper>
      ) : null}

      {task.isTemplate && !task.isRecurring && (
        <TemplateIndicatorWrapper>
          <Tooltip title="Task Template">
            <SaveTaskTemplateIcon />
          </Tooltip>
        </TemplateIndicatorWrapper>
      )}

      {!task.isTemplate && task.isRecurring && (
        <TemplateIndicatorWrapper>
          <Tooltip title="Recurring Task">
            <RecurringTaskIcon />
          </Tooltip>
        </TemplateIndicatorWrapper>
      )}

      {task.isTemplate && task.isRecurring && (
        <TemplateIndicatorWrapper>
          <Tooltip title="Recurring Task Template">
            <RecurringTaskTemplateIcon />
          </Tooltip>
        </TemplateIndicatorWrapper>
      )}

      {hasVariable && (
        <TemplateIndicatorWrapper>
          <Tooltip title="SmartFill">
            <SmartFill />
          </Tooltip>
        </TemplateIndicatorWrapper>
      )}

      <RichTextEditor
        className={`${styles['template-title-input']} ${styles['template-list-view']}`}
        data={task.taskName}
        readonly
        allowSimpleVariables
        disallowNewLine
        ref={updateRef}
        onInit={setData}
      />

      <MoreActions className="more__actions">
        <Tooltip title="Add task below">
          <Button
            icon={<ArrowDown16 />}
            size="small"
            type="text"
            onClick={handleAddTaskBelow}
            hideHoverBorderRadius
          />
        </Tooltip>
        {level < 3 && (
          <Tooltip title={addSubtaskTooltipText}>
            <Button
              icon={<AddRowBelowIcon />}
              size="small"
              type="text"
              onClick={handleAddSubTask}
              hideHoverBorderRadius
            />
          </Tooltip>
        )}
        <TaskMoreActions task={task} hideHoverBorderRadius />
      </MoreActions>
    </>
  );
};

export const RenderTaskName: FC<
  Omit<CellRendererParams, 'data'> & { data: ProjectTemplateTaskData }
> = ({ data, ...rest }) => {
  const task = useProjectTemplateTask(data.data.taskId);

  if (!task) {
    return null;
  }

  return <TaskNameCell task={task} level={data.level} data={data} {...rest} />;
};
